// Palettes.
//
// A camo is a pattern plus an ordered list of colours, and at 84x72 the colours
// matter more than the pattern. Three or four well separated tones read as a
// scheme at that size; eight subtle ones read as mud. Every palette here is
// short on purpose.
//
// MegaMek multiplies our colour by the sprite's grey level, so what a player
// picks is a ceiling rather than the final colour - a light palette darkens
// into shadow, a dark one goes nearly black. Mid-to-light tones survive best.

import { rng, clamp01 } from "./rng";

export type RGB = [number, number, number];

/**
 * The heading a preset sits under in the editor's picker. Sixteen presets fit
 * in one list; fifty do not, and the ones worth adding from here are faction
 * liveries, which come in obvious sets.
 */
export type PaletteGroup = "Classic" | "Inner Sphere" | "Clans" | "Pride";

/** Group order, which is the order the picker's chips are drawn in. */
export const PALETTE_GROUPS: PaletteGroup[] = [
  "Classic",
  "Inner Sphere",
  "Clans",
  "Pride",
];

export type Palette = {
  name: string;
  colors: RGB[];
  /**
   * Which chip shows this preset. Only presets have one: a generated palette,
   * an avatar's colours and a hand-edited palette are not in the picker and
   * belong to no group.
   */
  group?: PaletteGroup;
  /**
   * A flag rather than a colour scheme. Order is the design, so these are
   * excluded from anywhere a palette is picked at random - a flag is something
   * a person chooses on purpose, not something the page hands them.
   */
  flag?: boolean;
};

export const PRESETS: Palette[] = [
  {
    name: "Woodland",
    group: "Classic",
    colors: [
      [74, 83, 58],
      [104, 112, 78],
      [62, 54, 42],
      [38, 40, 32],
    ],
  },
  {
    name: "Desert",
    group: "Classic",
    colors: [
      [196, 172, 128],
      [163, 134, 92],
      [124, 106, 74],
      [92, 82, 62],
    ],
  },
  {
    name: "Urban",
    group: "Classic",
    colors: [
      [150, 152, 155],
      [104, 107, 112],
      [66, 69, 74],
      [38, 40, 44],
    ],
  },
  {
    name: "Arctic",
    group: "Classic",
    colors: [
      [226, 230, 234],
      [178, 188, 198],
      [126, 138, 150],
      [76, 86, 98],
    ],
  },
  {
    name: "Naval",
    group: "Classic",
    colors: [
      [70, 96, 122],
      [46, 66, 88],
      [110, 132, 152],
      [28, 38, 52],
    ],
  },
  {
    name: "Jungle",
    group: "Classic",
    colors: [
      [52, 92, 56],
      [78, 122, 66],
      [34, 62, 44],
      [116, 136, 76],
    ],
  },
  {
    name: "Rust",
    group: "Classic",
    colors: [
      [142, 78, 44],
      [104, 56, 34],
      [176, 116, 66],
      [62, 40, 30],
    ],
  },
  {
    name: "Ash",
    group: "Classic",
    colors: [
      [92, 88, 84],
      [58, 56, 54],
      [132, 126, 120],
      [32, 32, 32],
    ],
  },
  {
    name: "Ember",
    group: "Classic",
    colors: [
      [168, 62, 38],
      [206, 118, 44],
      [96, 38, 30],
      [48, 26, 24],
    ],
  },
  {
    name: "Void",
    group: "Classic",
    colors: [
      [44, 42, 62],
      [78, 72, 104],
      [26, 26, 38],
      [122, 112, 150],
    ],
  },
  {
    name: "Lance Blue",
    group: "Classic",
    colors: [
      [46, 84, 140],
      [86, 132, 190],
      [30, 52, 88],
      [180, 198, 220],
    ],
  },

  // Faction palettes.
  //
  // Measured off MegaMek's own camo art rather than written by hand, by
  // scripts/megamek-camo.mjs - the comment above each one names the file it
  // came from, so re-running the script reproduces these numbers and a
  // palette that reads wrong is fixed by pointing its entry at a better file.
  //
  // The art is not evenly useful. Most of the tree is one regiment's variant
  // of another regiment's scheme, so what a faction "looks like" is a choice
  // of source file, and the two the script cannot rescue - ComStar and Star
  // League - are marked where they occur.
  {
    name: "Draconis Combine",
    group: "Inner Sphere",
    // Draconis Combine/Sword of Light/Sorenson's Sabres.jpg
    // #8c1818 #972e1c #b78430 #d3a038
    colors: [
      [140, 24, 24],
      [151, 46, 28],
      [183, 132, 48],
      [211, 160, 56],
    ],
  },
  {
    name: "Federated Suns",
    group: "Inner Sphere",
    // Federated Suns/Davion Guards/Davion Guards.jpg
    // #bd0d0d #235899 #1f6098 #d1d1d1
    colors: [
      [189, 13, 13],
      [35, 88, 153],
      [31, 96, 152],
      [209, 209, 209],
    ],
  },
  {
    name: "Capellan Confederation",
    group: "Inner Sphere",
    // Capellan Confederation/Death Commandos.jpg
    // #363636 #3b4338 #3d4a38 #63a435
    colors: [
      [54, 54, 54],
      [59, 67, 56],
      [61, 74, 56],
      [99, 164, 53],
    ],
  },
  {
    name: "Lyran Commonwealth",
    group: "Inner Sphere",
    // Lyran Commonwealth/Lyran Guards/Lyran Guards.jpg
    // #2b61d3 #276cd3 #bdcde5 #ced1d4
    colors: [
      [43, 97, 211],
      [39, 108, 211],
      [189, 205, 229],
      [206, 209, 212],
    ],
  },
  {
    name: "Free Worlds League",
    group: "Inner Sphere",
    // Free Worlds League/Ducal Guard/1st Ducal Guard.jpg
    // #383838 #933dbb #9f55c0 #d1d1d1
    colors: [
      [56, 56, 56],
      [147, 61, 187],
      [159, 85, 192],
      [209, 209, 209],
    ],
  },
  {
    name: "ComStar",
    group: "Inner Sphere",
    // ComStar/Com Guards.jpg (flat; darkest slot rescued)
    // #1e1e24 #ced1d4 #d0d1d2 #d0d1d3
    colors: [
      [30, 30, 36],
      [206, 209, 212],
      [208, 209, 210],
      [208, 209, 211],
    ],
  },
  {
    name: "Free Rasalhague",
    group: "Inner Sphere",
    // Free Rasalhague Republic/Drakons.jpg
    // #803137 #525b87 #5a5f87 #6a70a3
    colors: [
      [128, 49, 55],
      [82, 91, 135],
      [90, 95, 135],
      [106, 112, 163],
    ],
  },
  {
    name: "Star League",
    group: "Inner Sphere",
    // Star League Defense Force/1st Royal BattleMech Regiment.jpg (flat; darkest slot rescued)
    // #182f13 #3a602e #546e26 #5b732c
    colors: [
      [24, 47, 19],
      [58, 96, 46],
      [84, 110, 38],
      [91, 115, 44],
    ],
  },
  {
    name: "Pirates",
    group: "Inner Sphere",
    // Pirates/Red Skull Legion.jpg
    // #2e2e2e #cc2f17 #d0321a #d0361e
    colors: [
      [46, 46, 46],
      [204, 47, 23],
      [208, 50, 26],
      [208, 54, 30],
    ],
  },
  {
    name: "Wolf",
    group: "Clans",
    // Clans/Wolf/Tactical Response Cluster.jpg
    // #c80c0b #5a6941 #7d7d7c #989898
    colors: [
      [200, 12, 11],
      [90, 105, 65],
      [125, 125, 124],
      [152, 152, 152],
    ],
  },
  {
    name: "Jade Falcon",
    group: "Clans",
    // Clans/Jade Falcon/Turkina Keshik.jpg
    // #238f20 #37c235 #52b498 #68cbae
    colors: [
      [35, 143, 32],
      [55, 194, 53],
      [82, 180, 152],
      [104, 203, 174],
    ],
  },
  {
    name: "Ghost Bear",
    group: "Clans",
    // Clans/Ghost Bear/Ourse Keshik.jpg
    // #557c98 #6985b2 #7094d0 #94a7c8
    colors: [
      [85, 124, 152],
      [105, 133, 178],
      [112, 148, 208],
      [148, 167, 200],
    ],
  },
  {
    name: "Smoke Jaguar",
    group: "Clans",
    // Clans/Smoke Jaguar/Shroud Keshik.jpg
    // #7b7b7b #c0c0c0 #cbcbcb #d1d1d1
    colors: [
      [123, 123, 123],
      [192, 192, 192],
      [203, 203, 203],
      [209, 209, 209],
    ],
  },
  {
    name: "Nova Cat",
    group: "Clans",
    // Clans/Nova Cat/Clan Nova Cat.jpg
    // #925e37 #d4cecf #d1d1d1 #cfd3d1
    colors: [
      [146, 94, 55],
      [212, 206, 207],
      [209, 209, 209],
      [207, 211, 209],
    ],
  },
  {
    name: "Snow Raven",
    group: "Clans",
    // Clans/Snow Raven/Corvus Keshik.jpg
    // #62517b #3664b2 #756a86 #9a9a9b
    colors: [
      [98, 81, 123],
      [54, 100, 178],
      [117, 106, 134],
      [154, 154, 155],
    ],
  },
  {
    name: "Steel Viper",
    group: "Clans",
    // Clans/Steel Viper/Omega Galaxy (Green).jpg
    // #494a4a #008024 #848483 #b1b2b5
    colors: [
      [73, 74, 74],
      [0, 128, 36],
      [132, 132, 131],
      [177, 178, 181],
    ],
  },
  {
    name: "Hell's Horses",
    group: "Clans",
    // Clans/Hell's Horses/Alpha Keshik.jpg
    // #b00d00 #494949 #9e8a2f #f5c206
    colors: [
      [176, 13, 0],
      [73, 73, 73],
      [158, 138, 47],
      [245, 194, 6],
    ],
  },
  {
    name: "Star Adder",
    group: "Clans",
    // Clans/Star Adder/Command Keshik.jpg
    // #3d3d3d #414342 #2e48a4 #2c4ca3
    colors: [
      [61, 61, 61],
      [65, 67, 66],
      [46, 72, 164],
      [44, 76, 163],
    ],
  },
  {
    name: "Coyote",
    group: "Clans",
    // Clans/Coyote/Golden Keshik.jpg
    // #c24823 #c15527 #c16930 #c0843c
    colors: [
      [194, 72, 35],
      [193, 85, 39],
      [193, 105, 48],
      [192, 132, 60],
    ],
  },
  {
    name: "Cloud Cobra",
    group: "Clans",
    // Clans/Cloud Cobra/Temple Keshik.jpg
    // #783985 #c47d5e #de9475 #52d0bf
    colors: [
      [120, 57, 133],
      [196, 125, 94],
      [222, 148, 117],
      [82, 208, 191],
    ],
  },
  {
    name: "Diamond Shark",
    group: "Clans",
    // Clans/Diamond Shark/Alpha Galaxy.jpg
    // #3d3e41 #2d4d9c #2e4e90 #2b539b
    colors: [
      [61, 62, 65],
      [45, 77, 156],
      [46, 78, 144],
      [43, 83, 155],
    ],
  },

  // Flag palettes.
  //
  // Four colours each, like every other preset, which makes each a reading of
  // a flag rather than a copy of one. Where the flag has more stripes than
  // that the least distinctive goes; where it has fewer, the colour the real
  // flag already repeats is repeated here.
  //
  // Order matters in these and in no other preset: the Pride pattern lays a
  // palette out as equal bands, in sequence.
  {
    name: "Pride",
    group: "Pride",
    flag: true,
    // Four stops across the six-stripe rainbow, far enough apart in hue that
    // it still reads as one at 84x72.
    colors: [
      [228, 3, 3],
      [255, 237, 0],
      [0, 128, 38],
      [0, 77, 255],
    ],
  },
  {
    name: "Bi Pride",
    group: "Pride",
    flag: true,
    // Magenta, lavender, blue, then a deep indigo. The flag has three stripes,
    // so the fourth is a darker tone of its own blue rather than a colour
    // borrowed from somewhere else.
    colors: [
      [214, 2, 112],
      [155, 79, 150],
      [0, 56, 168],
      [38, 34, 97],
    ],
  },
  {
    name: "Lesbian Pride",
    group: "Pride",
    flag: true,
    // The five-stripe flag without its white centre, which at this size reads
    // as a gap rather than as a stripe.
    colors: [
      [214, 41, 0],
      [255, 155, 85],
      [212, 97, 166],
      [165, 0, 98],
    ],
  },
  {
    name: "Trans Pride",
    group: "Pride",
    flag: true,
    // Blue, pink, white, pink. The repeat is not a slip: the flag repeats both
    // its pink and its blue, so banding this palette gives the real sequence
    // back rather than an abbreviation of it.
    colors: [
      [91, 206, 250],
      [245, 169, 184],
      [255, 255, 255],
      [245, 169, 184],
    ],
  },
  {
    name: "Nonbinary Pride",
    group: "Pride",
    flag: true,
    // Four stripes exactly, so this one is the flag and not a reading of it.
    colors: [
      [252, 244, 52],
      [255, 255, 255],
      [156, 89, 209],
      [44, 44, 44],
    ],
  },
  {
    name: "Pan Pride",
    group: "Pride",
    flag: true,
    // Three stripes, so the fourth is a deepened tone of its own blue - the
    // same move Bi Pride makes, rather than a colour borrowed from elsewhere.
    colors: [
      [255, 33, 140],
      [255, 216, 0],
      [33, 177, 255],
      [24, 92, 140],
    ],
  },
];

const hexPair = (v: number) => v.toString(16).padStart(2, "0");
export const toHex = ([r, g, b]: RGB): string =>
  `#${hexPair(r)}${hexPair(g)}${hexPair(b)}`;

export function fromHex(hex: string): RGB {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16) || 0,
    parseInt(h.slice(2, 4), 16) || 0,
    parseInt(h.slice(4, 6), 16) || 0,
  ];
}

// --- colour space ----------------------------------------------------------

export function rgbToHsl([r, g, b]: RGB): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  h = ((h % 1) + 1) % 1;
  s = clamp01(s);
  l = clamp01(l);
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}

/** Perceived brightness. Used to sort a palette dark-to-light. */
export const luma = ([r, g, b]: RGB): number =>
  0.2126 * r + 0.7152 * g + 0.0722 * b;

// --- generated palettes ----------------------------------------------------

type Scheme = "analogous" | "complement" | "triad" | "mono" | "split";
const SCHEMES: Scheme[] = ["analogous", "complement", "triad", "mono", "split"];

/**
 * A palette built around one hue by a colour-wheel relationship, rather than
 * four independent random colours. Independent randoms almost always look
 * accidental; a scheme almost always looks chosen.
 */
export function generatePalette(
  seed: number,
  count = 4,
  baseHue?: number,
): Palette {
  const rand = rng(seed);
  const scheme = SCHEMES[Math.floor(rand() * SCHEMES.length)]!;
  const h0 = baseHue ?? rand();
  const sat = 0.18 + rand() * 0.42;
  const colors: RGB[] = [];

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    let h = h0;
    switch (scheme) {
      case "analogous":
        h = h0 + (t - 0.5) * 0.16;
        break;
      case "complement":
        h = i % 2 === 0 ? h0 : h0 + 0.5;
        break;
      case "triad":
        h = h0 + (i % 3) / 3;
        break;
      case "split":
        h = i === 0 ? h0 : h0 + 0.5 + (i % 2 === 0 ? -0.08 : 0.08);
        break;
      case "mono":
        h = h0;
        break;
    }
    // Spread lightness across the range that survives being multiplied by a
    // sprite's grey level: nothing so dark it becomes black, nothing so light
    // it stops reading as a colour.
    const l = 0.22 + t * 0.5 + (rand() - 0.5) * 0.06;
    const s =
      scheme === "mono" ? sat * (0.6 + t * 0.6) : sat * (0.7 + rand() * 0.6);
    colors.push(hslToRgb(h, s, l));
  }
  colors.sort((a, b) => luma(a) - luma(b));
  return { name: "Generated", colors };
}

/**
 * Reduce an image to a few representative colours by k-means in RGB.
 *
 * Used for "match my avatar". Deliberately not a plain histogram: avatars are
 * mostly background, and the most *common* colour is rarely the one a person
 * thinks of as theirs.
 */
export function paletteFromImage(
  data: Uint8ClampedArray,
  count = 4,
  seed = 1,
): Palette {
  const samples: RGB[] = [];
  // Stride over the image rather than reading every pixel; k-means on a few
  // thousand samples lands in the same place and is instant.
  const step = Math.max(4, Math.floor(data.length / 4 / 4000)) * 4;
  for (let i = 0; i < data.length; i += step) {
    if (data[i + 3]! < 128) continue;
    samples.push([data[i]!, data[i + 1]!, data[i + 2]!]);
  }
  if (samples.length === 0) return generatePalette(seed, count);

  const rand = rng(seed);
  let centroids: RGB[] = Array.from(
    { length: count },
    () => samples[Math.floor(rand() * samples.length)]!,
  );

  for (let iter = 0; iter < 12; iter++) {
    const sums = Array.from(
      { length: count },
      () => [0, 0, 0, 0] as [number, number, number, number],
    );
    for (const s of samples) {
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < count; c++) {
        const d =
          (s[0] - centroids[c]![0]) ** 2 +
          (s[1] - centroids[c]![1]) ** 2 +
          (s[2] - centroids[c]![2]) ** 2;
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      sums[best]![0] += s[0];
      sums[best]![1] += s[1];
      sums[best]![2] += s[2];
      sums[best]![3]++;
    }
    centroids = sums.map((s, i) =>
      s[3] === 0
        ? centroids[i]!
        : ([
            Math.round(s[0] / s[3]),
            Math.round(s[1] / s[3]),
            Math.round(s[2] / s[3]),
          ] as RGB),
    );
  }

  // Nudge anything too dark or too washed out into the range that survives
  // MegaMek's multiply, without losing the hue that made it recognisable.
  const colors = centroids.map((c) => {
    const [h, s, l] = rgbToHsl(c);
    return hslToRgb(h, Math.max(s, 0.12), Math.min(Math.max(l, 0.18), 0.78));
  });
  colors.sort((a, b) => luma(a) - luma(b));
  return { name: "From avatar", colors };
}
